import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, Search, X } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

export default function Catalog() {
  const { brand: brandParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ categories: [] });
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState(searchParams.get("q") || "");
  const [category, setCategory] = useState("Semua");
  const [sort, setSort] = useState("default");

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data));
  }, []);

  useEffect(() => {
    setQ(searchParams.get("q") || "");
  }, [searchParams]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (brandParam) params.brand = brandParam;
    if (q) params.q = q;
    if (category !== "Semua") params.category = category;
    if (sort !== "default") params.sort = sort;
    api
      .get("/products", { params })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [brandParam, q, category, sort]);

  const title = useMemo(
    () => (brandParam ? `${decodeURIComponent(brandParam)}` : "Katalog Produk"),
    [brandParam]
  );

  const clearSearch = () => {
    setQ("");
    setSearchParams({});
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <Seo title={title} path={brandParam ? `/brand/${brandParam}` : "/katalog"} />

      <div className="mb-8">
        <p className="text-sm font-medium uppercase tracking-widest text-berry">
          {brandParam ? "Brand" : "Semua Produk"}
        </p>
        <h1 className="mt-1 font-heading text-4xl font-800 tracking-tight text-white">{title}</h1>
        <p className="mt-2 text-sm text-gray-400">
          {loading ? "Memuat..." : `${products.length} produk tersedia · Brand New & Bergaransi Resmi`}
        </p>
      </div>

      {/* Filter bar */}
      <div className="mb-8 flex flex-col gap-3 rounded-2xl border border-white/5 bg-[#12141C] p-4 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
          <Input
            data-testid="catalog-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari produk atau brand..."
            className="border-white/10 bg-white/5 pl-9 text-white placeholder:text-gray-500 focus-visible:ring-berry"
          />
          {q && (
            <button onClick={clearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-gray-400">
            <SlidersHorizontal className="h-4 w-4" />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="filter-category" className="w-[160px] border-white/10 bg-white/5 text-white">
              <SelectValue placeholder="Kategori" />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-[#1A1D24] text-white">
              <SelectItem value="Semua">Semua Kategori</SelectItem>
              {meta.categories.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="filter-sort" className="w-[150px] border-white/10 bg-white/5 text-white">
              <SelectValue placeholder="Urutkan" />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-[#1A1D24] text-white">
              <SelectItem value="default">Rekomendasi</SelectItem>
              <SelectItem value="name">Nama A-Z</SelectItem>
              <SelectItem value="price_asc">Harga Terendah</SelectItem>
              <SelectItem value="price_desc">Harga Tertinggi</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-80 rounded-2xl bg-white/5" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="rounded-2xl border border-white/5 bg-[#12141C] py-20 text-center">
          <p className="text-lg font-semibold text-white">Produk tidak ditemukan</p>
          <p className="mt-2 text-sm text-gray-400">Coba kata kunci atau filter lain.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
